<template>
    <div class="therapist-table justify-content-center">

        <search placeholder="Search"
                v-model="search"
                :placeholder="'user.type_the_therapist_name_to_search' | trans"
                :disabled="!isLoaded"
                v-on:enter="onSearchEnter"/>

        <table class="table table-sm">
            <thead class="thead-light">
            <tr>
                <th colspan="2" v-if="isMobile">{{'user.therapists' | trans}}</th>
                <th v-else>{{'user.therapists' | trans}}</th>
                <th class="text-center">{{'user.clients' | trans}}</th>
                <th class="text-center">{{'user.request_for_work' | trans}}</th>
                <th class="action-cell" v-if="!isMensurationForbidden"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="!isLoaded">
                <td colspan="4">
                    <loader class="table-loader" messages="home.labels.loading_message_table"></loader>
                </td>
            </tr>
            <tr v-for="item in items" :item="item" :key="item.id" v-if="isLoaded" is="item"
                v-on:deleteItem="onDeleteTherapist"></tr>
            </tbody>
        </table>

        <div class="text-center" v-if="isEmptyList && isLoaded">
            <h5>{{'bids.no_data' | trans }}</h5>
        </div>


        <div class="row mt-2 mt-md-0">
            <div class="col-md-12 col-lg-5">
                <nav class="nav justify-content-center justify-content-md-start" v-if="paginate.lastPage > 1">
                    <paginate :page-count="paginate.lastPage"
                              v-if="paginate.lastPage > 1"
                              :page-range="paginate.pageRange"
                              :margin-pages="paginate.marginPages"
                              :initial-page="paginate.page"
                              :force-page="paginate.forcePage"
                              :click-handler="onPaginationClick"
                              :container-class="'pagination pagination-sm'"
                              :page-class="'page-item'"
                              :page-link-class="'page-link'">
                        <span slot="prevContent" class="page-link">&laquo;</span>
                        <span slot="nextContent" class="page-link">&raquo;</span>
                    </paginate>
                </nav>
            </div>

            <div v-if="canCreateCurator && !isMensurationForbidden" class="col-md-12 col-lg-7 text-right">
                <mdc-button class="button-primary" raised @click="toggleAddTherapistModal">
                    {{ 'user.add_therapist' | trans }}
                </mdc-button>
            </div>
        </div>



        <add-therapist-modal :visible="isAddTherapistModalOpen"
                             v-on:close="toggleAddTherapistModal" v-on:accept="onAddTherapist">
        </add-therapist-modal>

        <delete-therapist-modal :visible="deleteModal.isOpen"
                                v-on:close="toggleDeleteModal" v-on:confirm="deleteTherapist">
        </delete-therapist-modal>
    </div>
</template>

<script>
  import Loader from '../../../../../loader/components/Loader';
  import isEmpty from 'lodash/isEmpty';
  import {pagination} from '../../../../../../common/mixins/pagination';
  import {mapGetters} from 'vuex';
  import Item from './TherapistItem';
  import ApiManager from '../../api/Manager';
  import DeleteTherapistModal from './modals/DeleteTherapistModal';
  import AddTherapistModal from './modals/AddTherapistModal';
  import Search from '../../../../../../common/table/Search';

  export default {
    props: {
      curatorId: {
        type: [Number, String],
        default() {
          return this.$route.params.id || null;
        },
      },
    },
    mixins: [pagination],
    data() {
      return {
        isLoaded: false,
        items: [],
        api: null,
        deleteModal: {
          id: null,
          isOpen: false,
        },
        isAddTherapistModalOpen: false,
        search: '',
      };
    },
    async created() {
      this.api = new ApiManager(this.curatorId);
      await this.getItems();
      this.isLoaded = true;
    },
    beforeDestroy() {
      this.api = null;
    },
    components: {
      Loader, Item, DeleteTherapistModal, AddTherapistModal, Search,
    },
    computed: {
      ...mapGetters('mobile', [
        'isMobile',
      ]),
      ...mapGetters('auth', [
        'canCreateCurator',
        'canUpdateCurator',
        'canDeleteCurator',
        'isMensurationForbidden',
      ]),
      isEmptyList: function() {
        return isEmpty(this.items);
      },
      lastPage() {
        return this.paginate.lastPage;
      },
      page() {
        return this.paginate.page;
      },
    },
    methods: {
      async getItems(reset = false) {
        try {
          let response = await this.api.getTherapists(this.paginate.perPage, reset ? 1 : this.paginate.page,
              this.search);
          let {items, perPage, lastPage, currentPage} = response.data.data;
          this.items = items;
          this.setPaginate({perPage, lastPage, currentPage});
        } catch (e) {
          console.error(e);
        }
      },
      async onPaginationClick(pageNum) {
        this.isLoaded = false;
        this.onPaginationBtnClick(pageNum);
        await this.getItems();
        this.isLoaded = true;
      },
      onDeleteTherapist(id) {
        this.deleteModal.id = id;
        this.toggleDeleteModal();
      },
      toggleDeleteModal() {
        this.deleteModal.isOpen = !this.deleteModal.isOpen;
      },
      async deleteTherapist() {
        this.isLoaded = false;
        try {
          await this.api.deleteTherapist(this.deleteModal.id);
          await this.getItems(true);
        } catch (e) {
          console.error(e);
        }
        this.toggleDeleteModal();
        this.deleteModal.id = null;
        this.isLoaded = true;
      },
      toggleAddTherapistModal() {
        this.isAddTherapistModalOpen = !this.isAddTherapistModalOpen;
      },
      async onAddTherapist() {
        this.isLoaded = false;
        this.toggleAddTherapistModal();
        await this.getItems(true);
        this.isLoaded = true;
      },
      async onSearchEnter(search) {
        this.isLoaded = false;
        this.search = search;
        await this.getItems(true);
        this.isLoaded = true;
      },
    },
    watch: {
      lastPage(newValue, oldValue) {
        // spike :
        if (newValue > 1 && oldValue === 1) {
          this.paginate.page = 0;
          this.paginate.forcePage = 0;
        }
      },
      page(newValue, oldValue) {
        // spike :
        if (newValue === 1 && oldValue > 1) {
          this.paginate.page = 0;
          this.paginate.forcePage = 0;
        }
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/modules/user/curator/profile/components/list/AttachedTherapistList.vue